import React from 'react';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';
import loadable from '@loadable/component';
import FullLayout from 'shared/layouts/FullLayout';
import { ServerError, UnauthorizedError, NotFoundError } from 'modules/error';
import { useAuth } from 'hooks';
import ShopFront from 'modules/shop-front/ShopFront';

import Contact from 'modules/shop-front/Contact';
import RollerBlindsPage from 'modules/shop-front/product-pages/Roller-Blind/RollerBlindsPage';
import PVCPlantationShutterPage from 'modules/shop-front/product-pages/Plantation-Shutters/PVCPlantationShutterPage';
import RollerShutterrPage from 'modules/shop-front/product-pages/RollerShutterrPage';
import ZipAwningPage from 'modules/shop-front/product-pages/ZipAwningPage';
import ShopFrontLayoutWrapper from 'shared/layouts/ShopFrontLayout';
import OrderSuccess from 'modules/order-management/create-order/OrderSuccess';
import VerticalBlindPage from 'modules/shop-front/product-pages/Vertical-Blind/VerticalBlindPage';
import VenetianBlindsPage from 'modules/shop-front/product-pages/VenetianBlindsPage';
import PanellGlidBlindsPage from 'modules/shop-front/product-pages/PanelGlideBlindsPage';
import RomanBlindsPage from 'modules/shop-front/product-pages/RomanBlindsPage';
import ZebraBlindsPage from 'modules/shop-front/product-pages/ZebraBlindsPage';
import CurversBlindsPage from 'modules/shop-front/product-pages/CurversBlindsPage';
import TimberPlantationShutterPage from 'modules/shop-front/product-pages/TimberPlantationShutterPage';
import AluminiumPlantationShutterPage from 'modules/shop-front/product-pages/AluminiumPlantationShutterPage';
import CurtainsPage from 'modules/shop-front/product-pages/Curtains/CurtainsPage';
import DoubleCurtainsPage from 'modules/shop-front/product-pages/Curtains/DoubleCurtain';
import ShopFrontAwningsPage from 'modules/shop-front/product-pages/ShopFrontAwningsPage';
import FoldingArmPage from 'modules/shop-front/product-pages/FoldingArmPage';
import ProtectaXLPage from 'modules/shop-front/product-pages/ProtectaXLPage';
import ProtectataMultiStopPage from 'modules/shop-front/product-pages/ProtectataMultiStopPage';
import ProtectaWireGuidePage from 'modules/shop-front/product-pages/ProtectaWireGuidePage';
import ProtectaPivotArmPage from 'modules/shop-front/product-pages/ProtectaPivotArmPage';
import ProtectaDeepChannelPage from 'modules/shop-front/product-pages/ProtectaDeepChannelPage';
import ProtectaStraightDropPage from 'modules/shop-front/product-pages/ProtectaStraightDropPage';
import RS42mmDL from 'modules/shop-front/product-pages/Roller-Shutters/42mmDL';
import RS42mmSL from 'modules/shop-front/product-pages/Roller-Shutters/42mmSL';
import RS36mm from 'modules/shop-front/product-pages/Roller-Shutters/36mm';
import RS55mmDL from 'modules/shop-front/product-pages/Roller-Shutters/55mm';
import RS50mmEx from 'modules/shop-front/product-pages/Roller-Shutters/50mmEX';
import PrivacyPolicy from 'modules/shop-front/PrivacyPolicy';
import AwningsList from 'modules/shop-front/product-categories-pages/AwningsList';
import BlindssList from 'modules/shop-front/product-categories-pages/BlindsList';
import CurtainsList from 'modules/shop-front/product-categories-pages/CurtainsList';
import PSList from 'modules/shop-front/product-categories-pages/PSList';
import RollerShuttersList from 'modules/shop-front/product-categories-pages/RollerShuttersList';

// Module check component is CanLoad and Permission
interface PrivateRouteConfig {
  isAuth?: boolean;
  canLoad?: boolean;
  permission?: string | string[];
  component: React.ReactElement;
}
const PrivatedRoute = (config: PrivateRouteConfig) => {
  const { component, isAuth } = config;
  if (isAuth) return component;
  return <Navigate to="/" replace={true} />;
};


// Lazy load component declaration
//const Dashboard = loadable(() => import('modules/dashboard'));
const ForgotPassword = loadable(() => import('modules/auth/forgot-password'));
const Login = loadable(() => import('modules/auth/login'));
const NewAccount = loadable(() => import('modules/auth/register'));
const ChangePassword = loadable(() => import('modules/auth/change-password'));
const SecureYourAccount = loadable(
  () => import('modules/auth/secure-your-account'),
);
const SelectSecure = loadable(
  () => import('modules/auth/secure-your-account'),
  {
    resolveComponent: (component) => component.SelectSecure,
  },
);
const CreateOrder = loadable(() => import('modules/order-management'), {
  resolveComponent: (component) => component.CreateOrder,
});
const EditOrder = loadable(() => import('modules/order-management'), {
  resolveComponent: (component) => component.EditOrder,
});
const OrderList = loadable(() => import('modules/order-management'), {
  resolveComponent: (component) => component.OrderList,
});
const OrderDetailById = loadable(() => import('modules/order-management'), {
  resolveComponent: (component) => component.OrderDetailById,
});

const UserList = loadable(() => import('modules/user-management'), {
  resolveComponent: (component) => component.UserList,
});

const UserDetail = loadable(() => import('modules/user-management'), {
  resolveComponent: (component) => component.UserDetail,
});

const UserEdit = loadable(() => import('modules/user-management'), {
  resolveComponent: (component) => component.UserEdit,
});

const PriceManagement = loadable(() => import('modules/product-management'), {
  resolveComponent: (component) => component.PriceManagement,
});



const createRoutes = (isAuth: boolean) => [
  {
    element: <FullLayout />,
    children: [
      
      {
        path: 'user',
        children: [
          {
            path: '',
            element: <PrivatedRoute isAuth={isAuth} component={<UserList />} />,
          },
          {
            path: ':user_id',
            element: (
              <PrivatedRoute isAuth={isAuth} component={<UserDetail />} />
            ),
          },
          {
            path: 'edit/:user_id',
            element: (
              <PrivatedRoute isAuth={isAuth} component={<UserEdit />} />
            ),
          },
        ]
      },
      {
        path: '/price_management',
        element: <PrivatedRoute isAuth={isAuth} component={<PriceManagement />} />
      },
      {
        path: '/order-success',
        element: <OrderSuccess />,
      },
      {
        path: 'order',
        children: [
          // {
          //   path: 'create',
          //   element: (
          //     <PrivatedRoute isAuth={isAuth} component={<CreateOrder />} />
          //   ),
          // },
          {
            path: 'edit/:id',
            element: (
              <PrivatedRoute isAuth={isAuth} component={<EditOrder />} />
            ),
          },
          {
            path: ':id',
            element: (
              <PrivatedRoute isAuth={isAuth} component={<OrderDetailById />} />
            ),
          },
          {
            path: '',
            element: (
              <PrivatedRoute isAuth={isAuth} component={<OrderList />} />
            ),
          },
        ],
      },
    ],
  },
  {
    element: <ShopFrontLayoutWrapper />,
    children: [
      {
        path: '/order/create',
        element: (
          <CreateOrder/>
        ),
      },
      {
        path: '/products/awnings',
        element: <AwningsList></AwningsList>,
      },
      {
        path: '/products/blinds',
        element: <BlindssList></BlindssList>,
      },
      {
        path: '/products/curtains',
        element: <CurtainsList></CurtainsList>,
      },
      {
        path: '/products/plantations',
        element: <PSList></PSList>,
      },
      {
        path: '/products/roller-shutters',
        element: <RollerShuttersList></RollerShuttersList>,
      },
      
      {
        path: '/roller-blinds',
        element: <RollerBlindsPage></RollerBlindsPage>,
      },
      {
        path: '/vertical-blinds',
        element: <VerticalBlindPage></VerticalBlindPage>,
      },
      {
        path: '/venetian-blinds',
        element: <VenetianBlindsPage></VenetianBlindsPage>,
      },
      {
        path: '/panel-glide-blinds',
        element: <PanellGlidBlindsPage></PanellGlidBlindsPage>,
      },
      {
        path: '/roman-blinds',
        element: <RomanBlindsPage></RomanBlindsPage>,
      },
      {
        path: '/zebra-blinds',
        element: <ZebraBlindsPage></ZebraBlindsPage>,
      },
      {
        path: '/curvers-blinds',
        element: <CurversBlindsPage></CurversBlindsPage>,
      },
      {
        path: '/pvc-plantation-shutters',
        element: <PVCPlantationShutterPage></PVCPlantationShutterPage>,
      },
      {
        path: '/timber-plantation-shutters',
        element: <TimberPlantationShutterPage></TimberPlantationShutterPage>,
      },
      {
        path: '/aluminium-plantation-shutters',
        element: <AluminiumPlantationShutterPage></AluminiumPlantationShutterPage>,
      },
      {
        path: '/curtains',
        element: <CurtainsPage></CurtainsPage>,
      },
      {
        path: '/double-curtains',
        element: <DoubleCurtainsPage></DoubleCurtainsPage>,
      },
      {
        path: '/roller-shutters',
        element: <RollerShutterrPage></RollerShutterrPage>,
      },
      {
        path: '/protecta-zip-awning',
        element: <ZipAwningPage></ZipAwningPage>,
      },
      {
        path: '/shop-front-awnings',
        element: <ShopFrontAwningsPage></ShopFrontAwningsPage>,
      },
      {
        path: '/folding-arm-awnings',
        element: <FoldingArmPage></FoldingArmPage>,
      },
      {
        path: '/protecta-xl',
        element: <ProtectaXLPage></ProtectaXLPage>,
      },
      {
        path: '/protecta-multi-stop',
        element: <ProtectataMultiStopPage></ProtectataMultiStopPage>,
      },
      {
        path: '/protecta-wire-guide',
        element: <ProtectaWireGuidePage></ProtectaWireGuidePage>,
      },
      {
        path: '/protecta-pivot-arm',
        element: <ProtectaPivotArmPage></ProtectaPivotArmPage>,
      },
      {
        path: '/protecta-deep-channel',
        element: <ProtectaDeepChannelPage></ProtectaDeepChannelPage>,
      },
      {
        path: '/protecta-straight-drop',
        element: <ProtectaStraightDropPage></ProtectaStraightDropPage>,
      },
      {
        path: '/RS42mmDL',
        element: <RS42mmDL></RS42mmDL>,
      },
      {
        path: '/RS42mmSL',
        element: <RS42mmSL></RS42mmSL>,
      },
      {
        path: '/RS36mm',
        element: <RS36mm></RS36mm>,
      },
      {
        path: '/RS55mmDL',
        element: <RS55mmDL></RS55mmDL>,
      },
      {
        path: '/RS50mmEx',
        element: <RS50mmEx></RS50mmEx>,
      },
      {
        path: '/contact',
        element: <Contact></Contact>,
      },
      {
        path: '/privacy',
        element: <PrivacyPolicy></PrivacyPolicy>,
      },
      
    ]
  },
  {
    path: '/',
    element: <ShopFront></ShopFront>,
  },
  
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/select-security',
    element: <SelectSecure />,
  },
  {
    path: '/register',
    element: <NewAccount />,
  },
  {
    path: '/change-password',
    element: <ChangePassword />,
  },
  {
    path: '/secure-your-account',
    element: <SecureYourAccount />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/server-error',
    element: <ServerError />,
  },
  {
    path: '/unauthorized',
    element: <UnauthorizedError />,
  },
  {
    path: '/not-found',
    element: <NotFoundError />,
  },
  {
    path: '*',
    element: <Navigate to={'/not-found'} replace />,
  },
  
];

const CustomBrowser = ()=>{
  const { isAuth } = useAuth();
  const router = createBrowserRouter(
    createRoutes(isAuth)
  );
  return(
    
    <RouterProvider router={router} />
  )
}
export default CustomBrowser;
